import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * `size` selects a density variant:
 * - `default` — standard form field (h-10, px-3). Use everywhere full-width forms live.
 * - `sm` — dense table/line-item field (h-8, px-2). Use inside inline line-item editor
 *   tables where columns are narrow; the lighter horizontal padding keeps short values
 *   (qty, UoM, HS code, container #) readable instead of clipped by the default px-3.
 *
 * The native HTML `size` attribute (visible width in characters) is intentionally omitted
 * — it is not used anywhere and would collide with this density prop.
 */
export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size?: 'default' | 'sm';
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size = 'default', ...props }, ref) => {
    return (
      <input
        type={type}
        // `dir="auto"` (overridable via props) makes typed content self-orient:
        // a Hebrew value lays out RTL, a number / SKU / email LTR, regardless of
        // the document's RTL base. Callers needing a fixed direction (e.g.
        // PhoneInput → ltr) can pass `dir` since it spreads after this.
        dir="auto"
        className={cn(
          'flex w-full rounded-md border border-input bg-background text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          size === 'sm' ? 'h-8 px-2 py-1' : 'h-10 px-3 py-2',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };
